import {
  arrivalClass,
  Badge,
  Banner,
  buttonClass,
  Card,
  CompareBars,
  cx,
  EmptyState,
  IconTile,
  PageHeader,
  RollingNumber,
  RowTitle,
  Skeleton,
  Stat,
  StateSwap,
  useArrivals,
  type CompareDatum,
} from '@ims/ui'
import {
  AlertTriangle,
  Bike,
  Clock,
  CheckCircle2,
  ChevronRight,
  CookingPot,
  FileText,
  HandCoins,
  Package,
  Receipt,
  ShoppingBag,
  Truck,
  ShoppingCart,
  TrendingUp,
  UtensilsCrossed,
  X,
} from '@ims/ui/icons'
import { orderTotals } from '@ims/core'
import type { Area } from '@ims/ui'
import { useState, type ComponentType, type ReactNode } from 'react'
import { Link } from 'react-router'
import { useBusinessMode, useBusinessName, useLive, useSettings } from '../hooks.ts'
import { dateLabel, dateShort, money, qty, todayIso } from '../lib/format.ts'
import { firstTimeToday, moveClick, useOpen } from '../lib/motion.ts'
import { registrationOf, taxNumberLabel } from '../lib/registration.ts'
import {
  addDays,
  calendarOf,
  daysBetween,
  hoursAgainstLastWeek,
  chartShort,
  chartTick,
  monthChart,
  monthStartOf,
  productLines,
  salesByHour,
  salesByService,
  salesSummary,
  shortDay,
  type ServiceKey,
  type SalesSummary,
} from '../lib/reports.ts'
import { useLookups } from '../components.tsx'
import { useCan, useWorkspace } from '../workspace.tsx'
import { CANCELLED_ROW, StatusBadge, balanceOf, useOpenBalances } from './invoices.tsx'
import { useAlerts } from '../layout/alerts.tsx'
import { StorageTipCard } from '../layout/storage.tsx'
import { WhatsNewCard } from './whats-new.tsx'
import { orderItemRows, tablesPath } from '../lib/orders.ts'
import { ridersCashTotal, useActiveDeliveries } from './deliveries.tsx'
import { ChartHead, RankRows, changeWords } from './report-parts.tsx'
import { useExpiringBatches } from './stock.tsx'

/** How each way of serving shows in "Sales by service". */
const SERVICE: Record<
  ServiceKey,
  { label: string; area: Area; icon: ComponentType<{ className?: string }> }
> = {
  dine_in: { label: 'Dine in', area: 'kitchen', icon: UtensilsCrossed },
  takeaway: { label: 'Takeaway', area: 'take', icon: ShoppingBag },
  delivery: { label: 'Delivery', area: 'insight', icon: Bike },
  counter: { label: 'Counter', area: 'sell', icon: ShoppingCart },
}

/** A stat card that opens its detail, for those who may see it (`to` null: just the card). */
function StatLink({ to, children }: { to: string | null; children: ReactNode }) {
  if (!to) return children
  return (
    <Link
      to={to}
      className="block rounded-card transition-shadow hover:shadow-card-hover [&>*]:h-full"
    >
      {children}
    </Link>
  )
}

export function DashboardScreen() {
  const settings = useSettings()
  const openItem = useOpen()
  // The month's chart rises once a day on this device; after that it only eases to new values.
  const [growChart] = useState(() => firstTimeToday('dashboard-chart'))
  const { current } = useWorkspace()
  const canReports = useCan('manager')
  // What is owed to suppliers: one statement, managers only.
  const payables = useLive(
    (repo) =>
      canReports && repo.payables ? repo.payables.totals(todayIso()) : Promise.resolve(null),
    [canReports],
    ['supplier_bills', 'supplier_payments', 'supplier_credits', 'supplier_allocations'],
  ).data
  const businessName = useBusinessName(current?.tenant.name)
  const today = todayIso()
  // Nepali or English month, as in Reports (Settings → Show dates in).
  const monthStart = monthStartOf(today, settings)
  // The same number of days at the start of last month, to compare like with like.
  const lastMonthEnd = addDays(monthStart, -1)
  const prevStart = monthStartOf(lastMonthEnd, settings)
  const prevTo = addDays(prevStart, daysBetween(monthStart, today))
  const prevEnd = prevTo > lastMonthEnd ? lastMonthEnd : prevTo
  const { restaurant } = useBusinessMode()
  const alerts = useAlerts()
  // Restaurant mode: the open orders, and today's bills by service and by hour.
  const service = useLive(
    async (repo) => {
      if (!restaurant) return null
      const [orders, sales, lastWeek] = await Promise.all([
        repo.orders.open(),
        canReports ? repo.reports.daySales(today) : [],
        // The same day last week, hour by hour, beside today's.
        canReports ? repo.reports.daySales(addDays(today, -7)) : [],
      ])
      const tickets = orders.flatMap((o) => o.tickets.filter((t) => !t.deleted_at))
      return {
        open: orders.length,
        unbilled: orders.reduce(
          (sum, o) => sum + orderTotals(orderItemRows(o.items)).totalPaisa,
          0,
        ),
        cooking: tickets.filter((t) => t.status !== 'ready').length,
        byService: salesByService(sales),
        byHour: hoursAgainstLastWeek(salesByHour(sales, new Date().getHours()), lastWeek),
      }
    },
    [restaurant, canReports, today],
    ['orders', 'order_items', 'kot_tickets', 'sales_invoices'],
  ).data
  const deliveryOn = restaurant && !!settings?.delivery_product_id
  const active = useActiveDeliveries()
  const withRiders = deliveryOn && active ? ridersCashTotal(active) : null
  const open = useOpenBalances()
  // Paperwork that is waiting on someone: quotations out, orders to bill, deliveries to come.
  const waiting = useLive(
    async (repo) => {
      if (!repo.reports) return null
      const [quotes, orders, purchases] = await Promise.all([
        restaurant ? null : repo.salesOrders.list({ kind: 'quotation', status: 'sent', limit: 1 }),
        restaurant ? [] : repo.reports.openSalesOrders(),
        repo.reports.openPurchaseOrders(),
      ])
      return { quotes: quotes?.total ?? 0, orders: orders.length, purchases: purchases.length }
    },
    [restaurant],
    [
      'sales_orders',
      'sales_order_lines',
      'sales_invoices',
      'sales_invoice_lines',
      'purchase_orders',
      'purchase_order_lines',
      'goods_receipt_lines',
    ],
  ).data
  const expiring = useExpiringBatches()
  const expired = (expiring ?? []).filter((r) => r.status === 'expired')
  const soon = (expiring ?? []).filter((r) => r.status === 'soon')

  const stats = useLive(
    async (repo) => {
      const [days, recent, products, levels, anyProduct, prevDays, sold] = await Promise.all([
        repo.reports.salesByDay(monthStart, today),
        repo.invoices.list({ limit: 8 }),
        repo.list('products', { where: { track_stock: true, is_active: true }, limit: 10000 }),
        repo.stock.levels(),
        repo.list('products', { limit: 1 }),
        // The chart and the top sellers are for people who can open Reports.
        canReports ? repo.reports.salesByDay(prevStart, prevEnd) : [],
        canReports ? repo.reports.productSales(monthStart, today) : [],
      ])
      const totals = new Map<string, number>()
      for (const l of levels)
        totals.set(l.product_id, (totals.get(l.product_id) ?? 0) + l.qty_milli)
      const low = products.rows.filter(
        (p) =>
          (totals.get(p.id) ?? 0) <= p.min_stock_milli &&
          (p.min_stock_milli > 0 || (totals.get(p.id) ?? 0) <= 0),
      )
      const negative = products.rows.filter((p) => (totals.get(p.id) ?? 0) < 0).length
      return {
        negative,
        totals,
        // Net of returns, like Reports.
        today: salesSummary(days.filter((d) => d.date === today)),
        month: salesSummary(days),
        days,
        prevDays,
        top: productLines(sold, new Map(products.rows.map((p) => [p.id, p.name])), 'amount')
          .lines.filter((l) => l.net > 0)
          .slice(0, 5),
        low,
        productCount: products.total,
        hasProducts: anyProduct.total > 0,
        recent: recent.rows,
      }
    },
    [today, monthStart, prevStart, prevEnd, canReports],
    ['sales_invoices', 'credit_notes', 'products', 'stock_movements'],
  ).data
  // A bill that reaches the list after it was shown (made on another device) slides in.
  const arrived = useArrivals(stats?.recent.map((i) => i.id) ?? [], stats !== undefined)
  const m = (paisa: number) => money(paisa, settings)
  /** "3 invoices", or "Rs. 452.00 billed, Rs. 113.00 returned" when something came back. */
  const salesHint = (s: SalesSummary) =>
    s.returns > 0
      ? `${m(s.sales)} billed, ${m(s.returns)} returned`
      : `${s.invoices} invoice${s.invoices === 1 ? '' : 's'}`
  // Balances need each invoice's payments: slower, so the other cards don't wait for it.
  const owed = useLive(
    async (repo) => {
      const month = await repo.invoices.list({
        status: 'finalized',
        from: monthStart,
        to: today,
        limit: 500,
      })
      const details = await Promise.all(month.rows.map((i) => repo.invoices.detail(i.id)))
      return details.reduce((a, d) => a + Math.max(0, d?.balancePaisa ?? 0), 0)
    },
    [today, monthStart],
    ['sales_invoices', 'payments', 'payment_allocations', 'credit_notes'],
  ).data

  const lookups = useLookups()
  const month = stats ? monthChart(stats.days, stats.prevDays, today, settings) : null
  const cal = calendarOf(settings)
  const short = chartShort
  const tick = chartTick
  const weekday = (iso: string) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString('en', { weekday: 'short' })
  const bills = (n: number) => `${n} bill${n === 1 ? '' : 's'}`
  const monthBars: CompareDatum[] =
    month?.days.map((d) => ({
      key: d.date,
      axis: d.day === 1 || d.day % 5 === 0 ? String(d.day) : undefined,
      title: `${weekday(d.date)} ${shortDay(d.date, cal)}`,
      value: d.net,
      compare: d.compare,
      note: d.net === null ? undefined : bills(d.invoices),
    })) ?? []
  const waitingCount =
    (payables && payables.owedPaisa > 0 ? 1 : 0) +
    (waiting ? waiting.quotes + waiting.orders + waiting.purchases : 0)

  return (
    <div>
      <PageHeader
        title={`Namaste, ${businessName}`}
        subtitle={dateLabel(today, settings)}
        actions={
          // Phones have Bill in the bottom bar.
          <Link to="/bill" className={buttonClass({ className: 'max-sm:hidden' })}>
            <ShoppingCart className="size-4" aria-hidden />
            New bill
          </Link>
        }
      />
      {stats && stats.negative > 0 && (
        <div className="mb-4 overflow-hidden rounded-card">
          <Banner
            tone="danger"
            action={
              <Link to="/stock?show=negative" className="font-semibold underline">
                Review
              </Link>
            }
          >
            <span className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>
                <span className="font-semibold">
                  {stats.negative} product{stats.negative === 1 ? ' is' : 's are'} below zero in
                  stock.
                </span>{' '}
                Usually a delivery that wasn’t entered, or a count that needs fixing.
              </span>
            </span>
          </Banner>
        </div>
      )}
      <WhatsNewCard />
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <StatLink to={canReports ? '/reports?period=today' : null}>
          <Stat
            label="Sales today"
            area="sell"
            value={stats && <RollingNumber value={stats.today.net} format={m} />}
            hint={stats && salesHint(stats.today)}
            icon={<TrendingUp className="size-5" />}
          />
        </StatLink>
        <StatLink to={canReports ? '/reports' : null}>
          <Stat
            label="This month"
            area="sell"
            value={stats && <RollingNumber value={stats.month.net} format={m} />}
            hint={stats && salesHint(stats.month)}
            icon={<FileText className="size-5" />}
          />
        </StatLink>
        {restaurant && (
          <StatLink to="/orders">
            <Stat
              label="Open orders"
              area="kitchen"
              value={service && <RollingNumber value={service.open} format={String} />}
              hint={
                alerts.count > 0
                  ? `${alerts.count} waiting: guests’ requests and ready tickets`
                  : service && service.open > 0
                    ? `${m(service.unbilled)} to bill${service.cooking > 0 ? `, ${service.cooking} in the kitchen` : ''}`
                    : 'Nothing waiting'
              }
              icon={<UtensilsCrossed className="size-5" />}
            />
          </StatLink>
        )}
        {deliveryOn ? (
          <StatLink to={tablesPath({ service_type: 'delivery' })}>
            <Stat
              label="Cash with riders"
              area="take"
              value={
                withRiders ? <RollingNumber value={withRiders.duePaisa} format={m} /> : undefined
              }
              hint={
                withRiders &&
                (withRiders.deliveries > 0
                  ? `From ${withRiders.deliveries} deliver${withRiders.deliveries === 1 ? 'y' : 'ies'}`
                  : 'Nothing to collect')
              }
              icon={<Bike className="size-5" />}
            />
          </StatLink>
        ) : (
          <StatLink to={canReports ? '/reports?tab=dues' : null}>
            <Stat
              label="Owed this month"
              area="people"
              value={owed !== undefined ? <RollingNumber value={owed} format={m} /> : undefined}
              hint="Unpaid balances on this month’s invoices"
              icon={<HandCoins className="size-5" />}
            />
          </StatLink>
        )}
        {!restaurant && (
          <StatLink to="/stock">
            <Stat
              label="Low or out of stock"
              area="stock"
              value={stats && <RollingNumber value={stats.low.length} format={String} />}
              hint={stats && `of ${stats.productCount} tracked products`}
              icon={<AlertTriangle className="size-5" />}
            />
          </StatLink>
        )}
      </div>
      {waitingCount > 0 && (
        <Card className="mt-4 p-0">
          <h2 className="px-5 pt-3 font-semibold text-ink">Waiting</h2>
          <div className="divide-y divide-border lg:grid lg:auto-cols-fr lg:grid-flow-col lg:divide-x lg:divide-y-0">
            {payables && payables.owedPaisa > 0 && (
              <WaitingLink
                to="/reports?tab=payables"
                area="buy"
                icon={HandCoins}
                label="To pay suppliers"
              >
                <span className="tabular block text-lg leading-tight font-bold text-ink">
                  {money(payables.owedPaisa, settings)}
                </span>
                {payables.overduePaisa > 0 && (
                  <span className="block text-sm font-semibold text-danger">
                    {money(payables.overduePaisa, settings)} is late
                  </span>
                )}
              </WaitingLink>
            )}
            {waiting && waiting.quotes > 0 && (
              <WaitingLink to="/quotations" area="sell" icon={FileText} label="Quotations">
                <WaitingWords>{waiting.quotes} with customers</WaitingWords>
              </WaitingLink>
            )}
            {waiting && waiting.orders > 0 && (
              <WaitingLink to="/orders" area="sell" icon={ShoppingBag} label="Orders">
                <WaitingWords>{waiting.orders} to bill</WaitingWords>
              </WaitingLink>
            )}
            {waiting && waiting.purchases > 0 && (
              <WaitingLink to="/purchase-orders" area="buy" icon={Truck} label="Purchase orders">
                <WaitingWords>{waiting.purchases} to arrive</WaitingWords>
              </WaitingLink>
            )}
          </div>
        </Card>
      )}
      <div className="mt-4 empty:hidden [&>*:last-child]:mb-0">
        {stats && settings && (
          <SetupChecklist
            tenantId={current?.tenant_id ?? ''}
            steps={[
              {
                label: 'Add your business details',
                detail: `${taxNumberLabel(registrationOf(settings))} and address, printed on every bill`,
                done: !!settings.pan_vat_number?.trim(),
                to: '/settings?tab=business',
              },
              {
                label: 'Add or import your products',
                detail: 'Type them in, scan barcodes, or paste from Excel',
                done: stats.hasProducts,
                to: '/products',
              },
              {
                label: 'Make your first bill',
                detail: 'Scan or search, then press F9',
                done: stats.recent.length > 0,
                to: '/bill',
              },
            ]}
          />
        )}
        <StorageTipCard />
      </div>
      {!stats ? (
        <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_340px]">
          <SkeletonCard rows={5} />
          <SkeletonCard rows={4} />
        </div>
      ) : (
        <>
          {canReports && service && service.byService.length > 0 && (
            <div className="mt-4 grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[340px_minmax(0,1fr)]">
              <Card className="p-0">
                <CardHead title="Sales by service today" />
                <ul className="divide-y divide-border">
                  {service.byService.map((row) => {
                    const { label, area, icon: Icon } = SERVICE[row.key]
                    return (
                      <li key={row.key} className="flex items-center gap-3 px-5 py-2.5 text-sm">
                        <IconTile area={area}>
                          <Icon className="size-5" />
                        </IconTile>
                        <span className="min-w-0 flex-1">
                          <span className="block font-medium">{label}</span>
                          <span className="block text-xs text-subtle">{bills(row.bills)}</span>
                        </span>
                        <span className="tabular shrink-0 font-semibold">{m(row.total)}</span>
                      </li>
                    )
                  })}
                </ul>
              </Card>
              <Card className="p-0">
                <CardHead title="Sales by hour today" />
                <div className="px-5 pt-4">
                  <CompareBars
                    data={service.byHour.map((h, i) => ({
                      key: String(h.hour),
                      axis: i % 2 === 0 ? h.label : undefined,
                      title: h.label,
                      value: h.total,
                      compare: h.compare,
                    }))}
                    format={m}
                    formatShort={short}
                    formatTick={tick}
                    seriesLabel="Today, by hour"
                    compareLabel="Same hour last week"
                    compareWords="same hour last week"
                    highlight={service.byHour.length - 1}
                    highlightLabel="Now"
                    best={false}
                    summary={`Bills by the hour today, ${service.byHour[0]?.label} to ${service.byHour.at(-1)?.label}.`}
                    height={140}
                  />
                </div>
              </Card>
            </div>
          )}
          {canReports && month && (stats.month.invoices > 0 || stats.month.returns > 0) && (
            <div className="mt-4 grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
              <Card className="p-0">
                <CardHead title="Sales this month" to="/reports" link="Reports" />
                <div className="px-5 pt-4">
                  <ChartHead
                    total={m(month.total)}
                    pill={changeWords(month.changePercent, month.prevMonthName)}
                    tone={(month.changePercent ?? 0) >= 0 ? 'success' : 'neutral'}
                  >
                    {month.today + 1} day{month.today === 0 ? '' : 's'} in ·{' '}
                    {month.pace !== null
                      ? `on pace for ${m(month.pace)} by ${month.lastDay}`
                      : `too early to compare with ${month.prevMonthName}`}
                  </ChartHead>
                  <CompareBars
                    className="mt-4"
                    data={monthBars}
                    format={m}
                    formatShort={short}
                    formatTick={tick}
                    seriesLabel={`${month.monthName}, by day`}
                    compareLabel={`Same day in ${month.prevMonthName}`}
                    compareWords={`same day in ${month.prevMonthName}`}
                    highlight={month.today}
                    summary={`Net sales by day this month: ${m(month.total)} in total.`}
                    height={170}
                    grow={growChart}
                  />
                </div>
              </Card>
              <Card className="p-0">
                <CardHead title="Top sellers this month" to="/reports?tab=products" />
                <RankRows
                  rows={stats.top.map((l) => ({
                    key: l.key,
                    name: l.name,
                    amount: m(l.net),
                    value: l.net,
                  }))}
                />
              </Card>
            </div>
          )}
          {(expired.length > 0 || soon.length > 0) && (
            <Card className="mt-4 p-0">
              <CardHead
                title="Expiry"
                extra={[
                  expired.length > 0 && `${expired.length} expired`,
                  soon.length > 0 &&
                    `${soon.length} within ${settings?.expiry_alert_days ?? 30} days`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                to="/stock"
              />
              <ul className="divide-y divide-border">
                {[...expired, ...soon].slice(0, 6).map((r) => (
                  <li key={r.batch.id}>
                    <Link
                      to={`/products/${r.product.id}`}
                      className="flex w-full items-center gap-3 px-5 py-2.5 text-left text-sm hover:bg-soft"
                    >
                      <RowTitle
                        tile={
                          <IconTile area="stock" size="sm">
                            <Clock className="size-4" />
                          </IconTile>
                        }
                        title={r.product.name}
                        sub={`Batch ${r.batch.batch_no}`}
                      />
                      <span
                        className={cx(
                          'ml-auto shrink-0 text-right font-semibold',
                          r.status === 'expired' ? 'text-danger' : 'text-warning',
                        )}
                        title={dateLabel(r.batch.expiry_date, settings)}
                      >
                        {r.status === 'expired' ? 'Expired' : 'Expires'}{' '}
                        {dateShort(r.batch.expiry_date, settings)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
            <Card className="p-0">
              <CardHead title="Recent invoices" to="/invoices" />
              {stats.recent.length === 0 ? (
                <EmptyState
                  icon={<FileText className="size-6" />}
                  title="No invoices yet"
                  description="Make your first sale from Quick bill."
                />
              ) : (
                <ul className="divide-y divide-border">
                  {stats.recent.map((inv) => (
                    <li
                      key={inv.id}
                      onClick={() => openItem(`/invoices/${inv.id}`)}
                      className={cx(
                        'flex cursor-pointer items-center gap-3 px-5 py-2.5 text-sm hover:bg-soft',
                        inv.status === 'cancelled' && CANCELLED_ROW,
                        arrived.has(inv.id) && arrivalClass,
                      )}
                    >
                      <RowTitle
                        tile={
                          // A phone needs the room for the number.
                          <IconTile area="sell" size="sm" className="max-sm:hidden">
                            <Receipt className="size-4" />
                          </IconTile>
                        }
                        title={
                          <Link
                            to={`/invoices/${inv.id}`}
                            className="hover:underline focus-visible:underline"
                            onClick={(e) => {
                              e.stopPropagation()
                              moveClick(`/invoices/${inv.id}`, 'open')(e)
                            }}
                          >
                            {inv.number ?? 'Not numbered yet'}
                          </Link>
                        }
                        sub={`${inv.customer_name ?? 'Walk-in'} · ${dateShort(inv.invoice_date, settings)}${
                          inv.finalized_at
                            ? `, ${new Date(inv.finalized_at).toLocaleTimeString('en', {
                                hour: 'numeric',
                                minute: '2-digit',
                              })}`
                            : ''
                        }`}
                      />
                      <span className="ml-auto flex shrink-0 flex-col items-end gap-1">
                        <span className="tabular font-semibold text-ink">
                          {money(inv.total_paisa, settings)}
                        </span>
                        <StateSwap state={`${inv.status}:${balanceOf(open, inv.id)}`}>
                          <StatusBadge invoice={inv} balancePaisa={balanceOf(open, inv.id)} />
                        </StateSwap>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <Card className="p-0">
              <CardHead title="Needs restocking" to="/stock" />
              {stats.low.length === 0 ? (
                <EmptyState
                  icon={
                    restaurant ? <CookingPot className="size-6" /> : <Package className="size-6" />
                  }
                  title="All stocked up"
                />
              ) : (
                <ul className="divide-y divide-border">
                  {stats.low.slice(0, 8).map((p) => {
                    const left = stats.totals.get(p.id) ?? 0
                    const unit = lookups.unit(p.unit_id)?.symbol
                    return (
                      <li key={p.id}>
                        <Link
                          to={`/products/${p.id}`}
                          className="flex w-full items-center gap-3 px-5 py-2.5 text-left text-sm hover:bg-soft"
                        >
                          <RowTitle
                            tile={
                              <IconTile area="stock" size="sm">
                                <Package className="size-4" />
                              </IconTile>
                            }
                            title={p.name}
                            sub={p.sku}
                          />
                          <span className="ml-auto flex shrink-0 flex-col items-end gap-1">
                            <span className="tabular font-semibold text-ink">
                              {qty(left)}
                              {unit ? ` ${unit}` : ''}
                            </span>
                            {left <= 0 ? (
                              <Badge tone="danger">Out of stock</Badge>
                            ) : (
                              <Badge tone="warning">Low · alert at {qty(p.min_stock_milli)}</Badge>
                            )}
                          </span>
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  )
}

/** A card's heading row: one style for every card, and the same words for its link. */
function CardHead({
  title,
  extra,
  to,
  link = 'See all',
}: {
  title: string
  /** Quieter words after the title: "1 expired · 1 within 30 days". */
  extra?: string
  to?: string
  link?: string
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
      <h2 className="min-w-0 font-semibold text-ink">
        {title}
        {extra && <span className="ml-2 text-sm font-normal text-muted">{extra}</span>}
      </h2>
      {to && (
        <Link
          to={to}
          className="tap-target inline-flex shrink-0 items-center gap-0.5 text-sm text-primary hover:underline"
        >
          {link}
          <ChevronRight className="size-4" aria-hidden />
        </Link>
      )}
    </div>
  )
}

/** One thing waiting on someone: money to pay, paperwork out. */
function WaitingLink({
  to,
  area,
  icon: Icon,
  label,
  children,
}: {
  to: string
  area: Area
  icon: ComponentType<{ className?: string }>
  label: string
  children: ReactNode
}) {
  return (
    <Link to={to} className="group flex items-center gap-3 px-5 py-3 hover:bg-soft">
      <IconTile area={area}>
        <Icon className="size-5" />
      </IconTile>
      <span className="min-w-0 flex-1">
        <span className="block text-sm text-muted">{label}</span>
        {children}
      </span>
      <ChevronRight className="size-4 shrink-0 text-muted transition-transform group-hover:translate-x-0.5" />
    </Link>
  )
}

function WaitingWords({ children }: { children: ReactNode }) {
  return <span className="block font-semibold text-ink">{children}</span>
}

function SkeletonCard({ rows }: { rows: number }) {
  return (
    <Card className="space-y-4" aria-busy="true">
      <Skeleton className="h-4 w-36" />
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="size-9 shrink-0 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-3/5" />
            <Skeleton className="h-2.5 w-2/5" />
          </div>
          <Skeleton className="h-3 w-16" />
        </div>
      ))}
    </Card>
  )
}

interface SetupStep {
  label: string
  detail: string
  done: boolean
  to: string
}

/** First-run steps, worked out from the data. Hidden once done or dismissed on this device. */
function SetupChecklist({ tenantId, steps }: { tenantId: string; steps: SetupStep[] }) {
  const key = `ims.setup-dismissed.${tenantId}`
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(key) === '1'
    } catch {
      return false
    }
  })
  const done = steps.filter((s) => s.done).length
  if (dismissed || done === steps.length) return null
  const dismiss = () => {
    setDismissed(true)
    try {
      localStorage.setItem(key, '1')
    } catch {
      // Storage blocked: hidden until the next visit.
    }
  }
  return (
    <Card className="mb-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-ink">Get your shop ready</h2>
          <p className="text-sm text-muted">
            {done} of {steps.length} done
          </p>
        </div>
        <button
          type="button"
          aria-label="Hide these steps"
          onClick={dismiss}
          className="tap-target rounded-lg p-1 text-muted hover:bg-soft hover:text-ink"
        >
          <X className="size-4" />
        </button>
      </div>
      <div
        className="mt-3 h-1.5 overflow-hidden rounded-full bg-primary-light"
        role="progressbar"
        aria-label="Setup progress"
        aria-valuemin={0}
        aria-valuemax={steps.length}
        aria-valuenow={done}
      >
        <div
          // Filled by transform, not width (DESIGN.md Motion).
          className="h-full w-full origin-left rounded-full bg-primary transition-transform duration-300 ease-out"
          style={{ transform: `scaleX(${done / steps.length})` }}
        />
      </div>
      <ul className="mt-2 divide-y divide-border">
        {steps.map((s) => (
          <li key={s.label}>
            {s.done ? (
              <div className="flex items-center gap-3 py-2.5 text-sm text-muted">
                <CheckCircle2 className="size-5 shrink-0 text-success" />
                <span className="line-through">{s.label}</span>
              </div>
            ) : (
              <Link
                to={s.to}
                className="group flex items-center gap-3 py-2.5 text-sm hover:text-primary"
              >
                <span className="size-5 shrink-0 rounded-full border-2 border-primary/40" />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{s.label}</span>
                  <span className="block text-xs text-muted">{s.detail}</span>
                </span>
                <ChevronRight className="size-4 shrink-0 text-muted transition-transform group-hover:translate-x-0.5" />
              </Link>
            )}
          </li>
        ))}
      </ul>
    </Card>
  )
}
